import React from 'react';
import ContentSection from '../components/sections/ContentSection';
import FeatureSection from '../components/sections/FeatureSection';

const AboutPage: React.FC = () => {
  // Features for ecosystem section
  const ecosystemFeatures = [
    {
      title: 'Business Requirements',
      description: 'AiDA adapts to your specific requirements, ensuring that the solution is tailored to your business objectives and customer expectations.'
    },
    {
      title: 'Business Processes',
      description: 'AiDA integrates with your established workflows and processes, enhancing efficiency without disrupting operations.'
    },
    {
      title: 'Business Data',
      description: 'Leveraging your existing business data, AiDA creates intelligent, context-aware responses that provide value to your customers.'
    },
    {
      title: 'AI Powered Business',
      description: 'The result is an AI Powered Business that operates with greater efficiency, responsiveness, and customer satisfaction.'
    }
  ];

  // Features for partners section
  const partnersFeatures = [
    {
      title: 'Business Partners',
      description: 'Organizations that share our vision for AI-enhanced customer experiences and are willing to adopt AiDA services.'
    },
    {
      title: 'Service Providers',
      description: 'Companies that can complement our offerings with additional expertise, technologies, or market reach.'
    },
    {
      title: 'Resource Contributors',
      description: 'Individuals and organizations that bring valuable insights, research, or tools to enhance the AiDA ecosystem.'
    }
  ];

  return (
    <>
      <div className="bg-gradient-to-r from-primary-800 to-primary-700 text-white py-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-accent-500 opacity-20 rounded-full blur-2xl"></div>
        <div className="container mx-auto px-4 relative">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">About AiDA Inc.</h1>
          <p className="text-xl max-w-3xl">
            Creating a thriving AI ecosystem that transforms how businesses engage with their customers.
          </p>
        </div>
      </div>
      
      <ContentSection 
        title="Company Vision and Mission"
        content="At AiDA Inc., our vision is to create a thriving AiDA Community with a robust ecosystem that transforms how businesses engage with their customers. We believe that artificial intelligence, when properly implemented, can bridge the gap between technological efficiency and human-like interaction, providing businesses with powerful tools that enhance rather than replace the human experience.\n\nOur mission is to empower businesses of all sizes with advanced AI solutions that deliver exceptional customer experiences while reducing operational costs and complexity. Through our AI Empowered Digital Assistant, we aim to revolutionize customer service by addressing the common pain points that frustrate both businesses and their customers."
        imageUrl="/assets/images/aida-vision.png"
      />
      
      <div className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">AiDA Ecosystem</h2>
          <p className="text-lg text-gray-600 max-w-4xl mx-auto">
            The AiDA Ecosystem is a comprehensive framework designed to integrate seamlessly with your existing business infrastructure. At its core, AiDA connects three essential elements:
          </p>
          <FeatureSection 
            title="" 
            features={ecosystemFeatures} 
          />
          <div className="flex items-center justify-center">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-accent-600">Industries We Serve</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center">
                  <span className="mr-2">•</span>
                  <span>Retail</span>
                </li>
                <li className="flex items-center">
                  <span className="mr-2">•</span>
                  <span>Travel</span>
                </li>
                <li className="flex items-center">
                  <span className="mr-2">•</span>
                  <span>Healthcare</span>
                </li>
                <li className="flex items-center">
                  <span className="mr-2">•</span>
                  <span>Finance</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">Partners and Collaboration</h2>
          <p className="text-lg text-gray-600 max-w-4xl mx-auto">
            We believe in the power of collaboration and partnership. AiDA Inc. actively seeks relationships with organizations that share our vision for AI-enhanced customer experiences and can contribute to our growing ecosystem.
          </p>
        </div>
      </div>
      
      <FeatureSection 
        title="Join Our Network" 
        features={partnersFeatures} 
      />
      
      <div className="bg-gray-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Join Our Journey</h2>
          <div className="max-w-3xl mx-auto">
            <p className="text-lg mb-6">
              We welcome investors who share our vision for the future of AI-powered business solutions. If you believe in the transformative potential of advanced AI assistants and want to be part of this exciting journey, we invite you to connect with us.
            </p>
            <p className="text-lg mb-6">
              We are also actively seeking business partners willing to adopt AiDA services, helping us refine and expand our offerings while benefiting from cutting-edge AI technology.
            </p>
            <p className="text-lg mb-8">
              For talented individuals with an entrepreneurial mindset, AiDA Inc. offers a dynamic environment where innovation is encouraged and impactful work is recognized. Join our team and help shape the future of AI-assisted customer engagement.
            </p>
            <a 
              href="/contact" 
              className="inline-block bg-accent-500 text-white hover:bg-accent-600 px-8 py-3 rounded-md font-medium transition-colors shadow-lg"
            >
              Contact Us
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default AboutPage;
